import { create } from 'zustand';
import { contractsApi } from '../lib/api';

/**
 * Store Zustand per gestire il workflow dei contracts (0-26)
 * Sostituisce il precedente sistema a 12 steps
 */
const useWorkflowStore = create((set, get) => ({
  // Stato
  currentProjectId: null,
  currentContract: 0,
  contracts: [], // Array di 27 contracts con stato
  executions: {}, // Map: contractNum -> array di executions
  selectedExecution: null,
  loading: false,
  executing: false,
  error: null,

  // Actions

  /**
   * Inizializza il workflow per un progetto
   */
  initWorkflow: async (projectId, project) => {
    set({ 
      currentProjectId: projectId,
      currentContract: project?.currentContract || 0,
      loading: true,
      error: null 
    });

    try {
      // Carica tutti i contracts del progetto
      const response = await contractsApi.getAll(projectId);
      const contracts = response.data;

      set({ 
        contracts,
        loading: false 
      });

      return contracts;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Carica le executions di un contract specifico
   */
  loadExecutions: async (contractNum) => {
    const { currentProjectId } = get();
    if (!currentProjectId) {
      throw new Error('Project ID not set');
    }

    set({ loading: true, error: null });

    try {
      const response = await contractsApi.getExecutions(currentProjectId, contractNum);
      const executions = response.data.data || response.data;

      set((state) => ({
        executions: {
          ...state.executions,
          [contractNum]: executions
        },
        loading: false
      }));

      return executions;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Esegue un contract
   */
  executeContract: async (contractNum, options = {}) => {
    const { currentProjectId } = get();
    if (!currentProjectId) {
      throw new Error('Project ID not set');
    }

    set({ executing: true, error: null });

    try {
      const response = await contractsApi.execute(currentProjectId, contractNum, options);
      // Il backend invia { success, message, data: {...execution} } — estraiamo l'esecuzione reale
      const execution = response.data?.data || response.data;

      // Aggiorna le executions
      set((state) => ({
        executions: {
          ...state.executions,
          [contractNum]: [execution, ...(state.executions[contractNum] || [])]
        },
        selectedExecution: execution,
        executing: false
      }));

      return execution;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, executing: false });
      throw error;
    }
  },

  /**
   * Approva un'execution
   */
  approveExecution: async (contractNum, execId, notes = null, curatedOutput = null) => {
    const { currentProjectId } = get();
    if (!currentProjectId) {
      throw new Error('Project ID not set');
    }

    set({ loading: true, error: null });

    try {
      const response = await contractsApi.approve(currentProjectId, contractNum, execId, notes, curatedOutput);
      const data = response.data;

      // Aggiorna il contract corrente e aggiorna l'execution approvata nello store
      const prevContractNum = get().currentContract;
      set((state) => ({
        currentContract: data.currentContract ?? prevContractNum,
        // Azzera selectedExecution: il ContractView del prossimo contract deve
        // mostrare lo stato vuoto, non la execution stale del contract approvato
        selectedExecution: null,
        // Aggiorna lo status nell'executions map così navigando indietro si vede 'approved'
        executions: data.execution ? {
          ...state.executions,
          [prevContractNum]: (state.executions[prevContractNum] || []).map(ex =>
            ex._id === execId ? { ...ex, status: 'approved' } : ex
          )
        } : state.executions,
        loading: false
      }));

      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Rigetta un'execution
   */
  rejectExecution: async (contractNum, execId, reason) => {
    const { currentProjectId } = get();
    if (!currentProjectId) {
      throw new Error('Project ID not set');
    }

    set({ loading: true, error: null });

    try {
      const response = await contractsApi.reject(currentProjectId, contractNum, execId, reason);
      const updatedExecution = response.data;

      // Aggiorna lo stato dell'execution
      set((state) => ({
        executions: {
          ...state.executions,
          [contractNum]: (state.executions[contractNum] || []).map(ex =>
            ex._id === execId ? updatedExecution : ex
          )
        },
        loading: false
      }));

      return updatedExecution;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Rigenera (crea alternative exploration)
   */
  regenerateContract: async (contractNum, execId, options = {}) => {
    const { currentProjectId } = get();
    if (!currentProjectId) {
      throw new Error('Project ID not set');
    }

    set({ executing: true, error: null });

    try {
      const response = await contractsApi.regenerate(currentProjectId, contractNum, execId, options);
      const newExecution = response.data?.data || response.data;

      // Aggiunge la nuova execution
      set((state) => ({
        executions: {
          ...state.executions,
          [contractNum]: [newExecution, ...(state.executions[contractNum] || [])]
        },
        selectedExecution: newExecution,
        executing: false
      }));

      return newExecution;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, executing: false });
      throw error;
    }
  },

  /**
   * Imposta direttamente un'execution come selezionata (senza fetch backend)
   */
  setSelectedExecution: (execution) => {
    set({ selectedExecution: execution });
  },

  /**
   * Seleziona un'execution specifica
   */
  selectExecution: async (contractNum, execId) => {
    const { currentProjectId } = get();
    if (!currentProjectId) {
      throw new Error('Project ID not set');
    }

    set({ loading: true, error: null });

    try {
      const response = await contractsApi.getExecution(currentProjectId, contractNum, execId);
      const execution = response.data;

      set({
        selectedExecution: execution,
        loading: false
      });

      return execution;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Naviga a un contract specifico
   */
  navigateToContract: (contractNum) => {
    set({ 
      currentContract: contractNum,
      selectedExecution: null 
    });
  },

  /**
   * Reset dello store
   */
  reset: () => {
    set({
      currentProjectId: null,
      currentContract: 0,
      contracts: [],
      executions: {},
      selectedExecution: null,
      loading: false,
      executing: false,
      error: null
    });
  },

  /**
   * Controlla se il job asincrono OpenAI è completato e scarica il risultato
   */
  checkAIStatus: async (contractNum) => {
    const { currentProjectId } = get();
    if (!currentProjectId) throw new Error('Project ID not set');

    set({ loading: true, error: null });

    try {
      const response = await contractsApi.checkAIStatus(currentProjectId, contractNum);
      const { status, execution } = response.data || response;

      if ((status === 'completed' || status === 'failed') && execution) {
        set((state) => ({
          executions: {
            ...state.executions,
            [contractNum]: (state.executions[contractNum] || []).map(ex =>
              ex._id === execution._id ? execution : ex
            )
          },
          selectedExecution: execution,
          loading: false
        }));
      } else {
        set({ loading: false });
      }

      return { status, execution };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Invia un singolo job a VEO
   */
  submitVeoJob: async (jobId, durationSeconds = null) => {
    const { currentProjectId } = get();
    if (!currentProjectId) throw new Error('Project ID not set');
    set({ loading: true, error: null });
    try {
      const response = await contractsApi.submitVeoJob(currentProjectId, jobId, durationSeconds);
      const { execution } = response.data || response;
      if (execution) {
        set((state) => ({
          executions: {
            ...state.executions,
            [16.4]: (state.executions[16.4] || []).map(ex =>
              ex._id === execution._id ? execution : ex
            )
          },
          selectedExecution: state.selectedExecution?._id === execution._id
            ? execution
            : state.selectedExecution,
          loading: false
        }));
      } else {
        set({ loading: false });
      }
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Invia il prossimo clip di estensione a VEO per una variante già avviata
   */
  extendVeoJob: async (jobId, durationSeconds = null) => {
    const { currentProjectId } = get();
    if (!currentProjectId) throw new Error('Project ID not set');
    set({ loading: true, error: null });
    try {
      const response = await contractsApi.extendVeoJob(currentProjectId, jobId, durationSeconds);
      const { execution } = response.data || response;
      if (execution) {
        set((state) => ({
          executions: {
            ...state.executions,
            [16.4]: (state.executions[16.4] || []).map(ex =>
              ex._id === execution._id ? execution : ex
            )
          },
          selectedExecution: state.selectedExecution?._id === execution._id
            ? execution
            : state.selectedExecution,
          loading: false
        }));
      } else {
        set({ loading: false });
      }
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Scarta il risultato video VEO già ricevuto
   */
  discardVeoVideo: async () => {
    const { currentProjectId } = get();
    if (!currentProjectId) throw new Error('Project ID not set');
    set({ loading: true, error: null });
    try {
      const response = await contractsApi.discardVeoVideo(currentProjectId);
      const { execution } = response.data || response;
      if (execution) {
        set((state) => ({
          executions: {
            ...state.executions,
            [16.4]: (state.executions[16.4] || []).map(ex =>
              ex._id === execution._id ? execution : ex
            )
          },
          selectedExecution: state.selectedExecution?._id === execution._id
            ? execution
            : state.selectedExecution,
          loading: false
        }));
      } else {
        set({ loading: false });
      }
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Ricostruisce la lista veo_jobs sull'execution approvata di C16.4
   */
  rebuildVeoJobs: async () => {
    const { currentProjectId } = get();
    if (!currentProjectId) throw new Error('Project ID not set');
    set({ loading: true, error: null });
    try {
      const response = await contractsApi.rebuildVeoJobs(currentProjectId, true);
      const { execution } = response.data || response;
      if (execution) {
        set((state) => ({
          executions: {
            ...state.executions,
            [16.4]: (state.executions[16.4] || []).map(ex =>
              ex._id === execution._id ? execution : ex
            )
          },
          selectedExecution: state.selectedExecution?._id === execution._id
            ? execution
            : state.selectedExecution,
          loading: false
        }));
      } else {
        set({ loading: false });
      }
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Reinvia i job a VEO (nuova operation)
   */
  resubmitVeo: async (selectedVariants = null) => {
    const { currentProjectId } = get();
    if (!currentProjectId) throw new Error('Project ID not set');
    set({ loading: true, error: null });
    try {
      const response = await contractsApi.resubmitVeo(currentProjectId, selectedVariants);
      const { execution } = response.data || response;
      if (execution) {
        set((state) => ({
          executions: {
            ...state.executions,
            [16.4]: (state.executions[16.4] || []).map(ex =>
              ex._id === execution._id ? execution : ex
            )
          },
          selectedExecution: state.selectedExecution?._id === execution._id
            ? execution
            : state.selectedExecution,
          loading: false
        }));
      } else {
        set({ loading: false });
      }
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Controlla lo stato di tutti i job VEO di C16.4 (manuale, no polling)
   */
  checkVeoStatus: async () => {
    const { currentProjectId } = get();
    if (!currentProjectId) throw new Error('Project ID not set');

    set({ loading: true, error: null });
    try {
      const response = await contractsApi.checkVeoStatus(currentProjectId);
      const { summary, execution } = response.data || response;

      if (execution) {
        set((state) => ({
          executions: {
            ...state.executions,
            [16.4]: (state.executions[16.4] || []).map(ex =>
              ex._id === execution._id ? execution : ex
            )
          },
          selectedExecution: state.selectedExecution?._id === execution._id
            ? execution
            : state.selectedExecution,
          loading: false
        }));
      } else {
        set({ loading: false });
      }

      return { summary };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Elimina un clip VEO dalla variante (rimuove da inputData e outputData)
   */
  deleteVeoClip: async (jobId, clipIndex) => {
    const { currentProjectId } = get();
    if (!currentProjectId) throw new Error('Project ID not set');
    set({ loading: true, error: null });
    try {
      const response = await contractsApi.deleteVeoClip(currentProjectId, jobId, clipIndex);
      const { execution } = response.data || response;
      if (execution) {
        set((state) => ({
          executions: {
            ...state.executions,
            [16.4]: (state.executions[16.4] || []).map(ex =>
              ex._id === execution._id ? execution : ex
            )
          },
          selectedExecution: state.selectedExecution?._id === execution._id
            ? execution
            : state.selectedExecution,
          loading: false
        }));
      } else {
        set({ loading: false });
      }
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  regenerateVeoClip: async (jobId, clipIndex, durationSeconds = null) => {
    const { currentProjectId } = get();
    if (!currentProjectId) throw new Error('Project ID not set');
    set({ loading: true, error: null });
    try {
      const response = await contractsApi.regenerateVeoClip(currentProjectId, jobId, clipIndex, durationSeconds);
      const { execution } = response.data || response;
      if (execution) {
        set((state) => ({
          executions: {
            ...state.executions,
            [16.4]: (state.executions[16.4] || []).map(ex =>
              ex._id === execution._id ? execution : ex
            )
          },
          selectedExecution: state.selectedExecution?._id === execution._id
            ? execution
            : state.selectedExecution,
          loading: false
        }));
      } else {
        set({ loading: false });
      }
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Aggiorna il testo di un clip VEO e resetta il suo stato a queued
   */
  updateVeoClip: async (jobId, clipIndex, text, instruction) => {
    const { currentProjectId } = get();
    if (!currentProjectId) throw new Error('Project ID not set');
    set({ loading: true, error: null });
    try {
      const response = await contractsApi.updateVeoClip(currentProjectId, jobId, clipIndex, text, instruction);
      const { execution } = response.data || response;
      if (execution) {
        set((state) => ({
          executions: {
            ...state.executions,
            [16.4]: (state.executions[16.4] || []).map(ex =>
              ex._id === execution._id ? execution : ex
            )
          },
          selectedExecution: state.selectedExecution?._id === execution._id
            ? execution
            : state.selectedExecution,
          loading: false
        }));
      } else {
        set({ loading: false });
      }
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Cancella dal DB tutte le execution dal contract indicato in poi e resetta il progetto
   */
  resetContractsFrom: async (contractNum) => {
    const { currentProjectId } = get();
    if (!currentProjectId) throw new Error('Project ID not set');
    set({ loading: true, error: null });
    try {
      const response = await contractsApi.resetContractsFrom(currentProjectId, contractNum);
      // Rimuove dal cache locale le executions dei contracts >= contractNum
      set((state) => {
        const newExecutions = {};
        for (const [key, val] of Object.entries(state.executions)) {
          if (parseFloat(key) < contractNum) {
            newExecutions[key] = val;
          }
        }
        return {
          executions: newExecutions,
          currentContract: contractNum,
          selectedExecution: null,
          loading: false
        };
      });
      return response.data || response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      set({ error: errorMessage, loading: false });
      throw error;
    }
  },

  /**
   * Clear errori
   */
  clearError: () => set({ error: null })
}));

export default useWorkflowStore;
